import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import authService from '../../services/authService'
import { useAuthStore } from '../../features/auth/stores/authStore'
import { cacheCurrentUser } from '../../features/profile/hooks/useCurrentUser'
import { userService } from '../../features/onboarding'
import { getUserFromToken } from '../../features/auth/utils/tokenUtils'
import { trackLogin, trackSignup, setUserId, trackError } from '../../shared/utils/analytics'
import { fbTrackLogin, fbTrackSignup, fbTrackLead } from '../../shared/utils/facebookPixel'
import './Auth.css'

const OAuthCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setTokens, checkAuth, logout } = useAuthStore()
  const [error, setError] = useState<string | null>(null)
  const [isBlockedAccount, setIsBlockedAccount] = useState(false)
  const [isProcessing, setIsProcessing] = useState(true)
  const processedRef = useRef(false)

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution (React Strict Mode)
      if (processedRef.current) {
        return
      }

      const code = searchParams.get('code')
      const errorParam = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (errorParam) {
        processedRef.current = true
        setError(errorDescription || errorParam)
        setIsProcessing(false)
        return
      }

      if (!code) {
        // Don't set error immediately - URL params might not be ready yet
        // Wait a brief moment before declaring no code
        setTimeout(() => {
          if (!processedRef.current && !searchParams.get('code')) {
            processedRef.current = true
            setError('No authorization code received')
            setIsProcessing(false)
          }
        }, 100)
        return
      }

      // Mark as processed before async operation
      processedRef.current = true

      try {
        // Exchange authorization code for tokens
        const tokens = await authService.exchangeCodeForTokens(code)
        setTokens(tokens)
        checkAuth()

        // Check if this email is allowed for student signup/login
        const userInfo = getUserFromToken(tokens.IdToken || null)
        if (userInfo?.email) {
          try {
            const emailCheck = await userService.checkEmailAvailability(userInfo.email)
            if (!emailCheck.studentSignupAllowed) {
              const rawType = emailCheck.accountType
              const accountTypeLabel =
                rawType && typeof rawType === 'string'
                  ? rawType
                      .split('_')
                      .map((w: string) => w.charAt(0) + w.slice(1).toLowerCase())
                      .join(' ')
                  : 'another account type'
              // Sign out since this email is not allowed — use store logout to
              // clear both raw localStorage tokens and the Zustand persist state
              logout()
              setIsBlockedAccount(true)
              setError(
                `This email is already registered as ${accountTypeLabel}, please use a different email.`
              )
              setIsProcessing(false)
              return
            }
          } catch (checkErr) {
            // If the check-email API fails, log and proceed
            // (don't block login due to a secondary service failure)
            console.warn('Email availability check failed, proceeding:', checkErr)
          }
        }

        // Call onboard endpoint to check/create user profile
        try {
          const onboardResponse = await userService.onboardUser()

          cacheCurrentUser(onboardResponse.data)

          // Track successful Google auth
          if (onboardResponse.isNewUser) {
            // Get UTM source from localStorage (stored on landing page)
            const utmSource = localStorage.getItem('utm_source') || undefined
            const studentClass = onboardResponse.data?.profileMetadata?.studentClass
            trackSignup({
              signup_method: 'google',
              student_class: studentClass,
              source: utmSource,
            })
            fbTrackSignup('google')
            fbTrackLead('signup_google') // FB Pixel Lead event for new signups
          } else {
            trackLogin('google')
            fbTrackLogin('google')
          }

          // Set user ID for analytics
          if (onboardResponse.data?.email) {
            setUserId(onboardResponse.data.email)
          }

          // Navigate based on profile completion status
          const redirectTo = sessionStorage.getItem('auth_redirect') || '/dashboard'
          sessionStorage.removeItem('auth_redirect')

          if (onboardResponse.isNewUser || !onboardResponse.profileComplete) {
            // New user or incomplete profile -> go to profile completion, carry redirect target
            navigate('/onboarding', { replace: true, state: { redirectTo } })
          } else {
            // Existing user with complete profile -> go to intended destination
            navigate(redirectTo, { replace: true })
          }
        } catch (onboardErr) {
          // If onboard API fails (e.g., backend not deployed), proceed to onboarding
          // This allows the app to work even if the onboard endpoint isn't ready
          console.warn('Onboard API call failed, proceeding to onboarding:', onboardErr)
          navigate('/onboarding', { replace: true })
        }
      } catch (err) {
        console.error('OAuth callback error:', err)
        const message = err instanceof Error ? err.message : 'Failed to complete sign in'
        trackError('oauth_callback', message, 'google_login')
        setError(message)
        setIsProcessing(false)
      }
    }

    handleCallback()
  }, [searchParams, navigate, setTokens, checkAuth, logout])

  // Only show error if we're done processing and have an error
  if (!isProcessing && error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>My NEET Buddy</h1>
          <h2>Sign In Failed</h2>
          <div className="error-message">{error}</div>
          {isBlockedAccount ? (
            // Redirect through Cognito logout to clear the Hosted UI SSO session,
            // so the Google account picker appears on the next attempt.
            <button
              className="btn-primary"
              onClick={() => { window.location.href = authService.getCognitoLogoutUrl() }}
            >
              Try a different account
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to Login
            </button>
          )}
        </div>
      </div>
    )
  }

  // Show loading while processing
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>My NEET Buddy</h1>
        <h2>Signing you in...</h2>
        <div className="oauth-loading">
          <div className="spinner"></div>
          <p>Please wait while we complete your sign in.</p>
        </div>
      </div>
    </div>
  )
}

export default OAuthCallback
